
import { useQuery, QueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface MemorialStorageData {
  totalBytes: number;
  formattedSize: string;
}

export const useMemorialStorage = (memorialId?: string) => {
  return useQuery({
    queryKey: ['memorial-storage', memorialId],
    queryFn: async (): Promise<MemorialStorageData> => {
      if (!memorialId) {
        return { totalBytes: 0, formattedSize: '0 B' };
      }

      const { data, error } = await supabase
        .rpc('calculate_memorial_storage', { memorial_uuid: memorialId });

      if (error) {
        console.error('[useMemorialStorage] Error calculating storage:', error);
        throw new Error(`Failed to calculate storage: ${error.message}`);
      }

      const totalBytes = data || 0;
      const formattedSize = formatBytes(totalBytes);

      return { totalBytes, formattedSize };
    },
    enabled: !!memorialId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
  });
};

/**
 * Format bytes to human readable format
 */
export const formatBytes = (bytes: number): string => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  const size = parseFloat((bytes / Math.pow(k, i)).toFixed(2));
  return `${size} ${sizes[i]}`;
};

/**
 * Recalculate storage for a memorial and update the cache
 */
export const recalculateStorage = async (
  memorialId: string,
  queryClient: QueryClient
): Promise<void> => {
  const { data, error } = await supabase
    .rpc('calculate_memorial_storage', { memorial_uuid: memorialId });

  if (error) {
    console.error('[recalculateStorage] Error:', error);
    throw error;
  }

  const totalBytes = data || 0;
  const formattedSize = formatBytes(totalBytes);

  // Update the cache directly with fresh data
  queryClient.setQueryData(['memorial-storage', memorialId], {
    totalBytes,
    formattedSize
  });
};
